import type { ReactElement } from "react"
import { Navigate, Outlet } from "react-router"

import { orgIsInternalAdmin } from "@/api/orgs"
import { AccessDeniedPage } from "@/components/global/Errors/AccessDeniedPage"
import { NotFound404Page } from "@/components/NotFound404Page"
import { getAdminSubdomainUrl } from "@/util/appUrls"
import { showInternalTools, viewManager } from "@/util/viewManager"

const isUserAuthenticated = () => {
  return Boolean(viewManager.get("user"))
}

export const isOneSchemaUser = () => {
  return viewManager.get("user")?.email?.endsWith("@oneschema.co")
}

export function ProtectedRoute(): ReactElement | null {
  if (!isUserAuthenticated()) {
    return <Navigate to="/login" replace />
  }
  return <Outlet />
}

function isOnAdminOrg() {
  const org = viewManager.get("org")
  return org && orgIsInternalAdmin(org)
}

/**
 * Wrapper for routes that should only be accessible by internal admins when on
 * the `admin` org.
 *
 * On non-admin orgs, internal admins are redirected to the admin subdomain
 * (full page navigation, since it's a different origin).
 */
export function InternalAdminOrgOnlyRoute(): ReactElement | null {
  if (showInternalTools()) {
    if (!isOnAdminOrg()) {
      // Full-page redirect to the admin subdomain, preserving the full URL
      // path including query string and hash. We use replace() so the current
      // history entry is replaced, avoiding a back-button redirect loop.
      // NOTE: React Router's <Navigate> only handles app-internal paths,
      // so we use window.location for this cross-origin redirect.
      const { pathname, search, hash } = window.location
      window.location.replace(getAdminSubdomainUrl(`${pathname}${search}${hash}`))
      return null
    }
    return <Outlet />
  }
  return <NotFound404Page />
}

interface ProtectedAccessRouteProps {
  canAccess: boolean
}

export function ProtectedAccessRoute({
  canAccess,
}: ProtectedAccessRouteProps): ReactElement | null {
  if (canAccess) {
    return <Outlet />
  }
  return <AccessDeniedPage />
}
